import type { ReactElement } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { BackButton } from '@/components/common/BackButton';
import { CardList } from '@/components/result/CardList';
import { DesktopResultAside } from '@/components/result/DesktopResultAside';
import { RatingRow } from '@/components/result/RatingRow';
import { ResultBlock } from '@/components/result/ResultBlock';
import { useReading } from '@/hooks/api/useReadings';
import { toTarotCardItems } from '@/api/selectors';
import { parseInterpretation, type ParsedCard } from '@/api/interpretationParser';
import { formatSpreadCompletedRu, t } from '@/i18n';
import anim from '@/styles/animations.module.css';
import styles from './ResultPage.module.css';
import type { components } from '@/api/schema';
import type { TarotCardItem } from '@/types';

type ReadingDetail = components['schemas']['ReadingDetailResponse'];

const SPARKLES = String.fromCodePoint(0x2728);
const CYCLE = String.fromCodePoint(0x1f504);
const CRYSTAL_BALL = String.fromCodePoint(0x1f52e);

function parsedCardToItem(card: ParsedCard): TarotCardItem {
  return {
    emoji: card.emoji,
    name: card.name,
    description: card.meaning.replace(/<[^>]+>/g, '').replace(/\s*\n\s*/g, ' ').trim(),
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function allowOnlyBTags(raw: string): string {
  const B_OPEN = '__ORACLE_B_OPEN__';
  const B_CLOSE = '__ORACLE_B_CLOSE__';

  const withTokens = raw
    .replace(/<\s*b\s*>/gi, B_OPEN)
    .replace(/<\s*b\s+[^>]*>/gi, B_OPEN)
    .replace(/<\s*\/\s*b\s*>/gi, B_CLOSE);

  return escapeHtml(withTokens).replaceAll(B_OPEN, '<b>').replaceAll(B_CLOSE, '</b>');
}

function interpretationToHtml(text: string): string {
  const normalized = text.replace(/\r\n/g, '\n');
  const base = allowOnlyBTags(normalized);
  const paragraphs = base
    .split(/\n{2,}/g)
    .map((p) => p.replace(/\n/g, '<br/>'))
    .join('</p><p>');

  return `<p>${paragraphs}</p>`;
}

function parseReadingId(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function InterpretationBlocks({ reading }: { reading: ReadingDetail }) {
  const parsed = parseInterpretation(reading.interpretation);

  if (parsed) {
    const cards = parsed.cards.map(parsedCardToItem);
    return (
      <>
        {parsed.description ? (
          <ResultBlock
            icon={SPARKLES}
            label={t.result.descriptionHeading}
            text={interpretationToHtml(parsed.description)}
          />
        ) : null}

        {cards.length > 0 ? (
          <CardList cards={cards} heading={t.result.cardsHeading} headingIcon={SPARKLES} />
        ) : null}

        {parsed.interaction ? (
          <ResultBlock
            icon={CYCLE}
            label={t.result.interactionHeading}
            text={interpretationToHtml(parsed.interaction)}
          />
        ) : null}

        {parsed.forecast ? (
          <ResultBlock
            icon={CRYSTAL_BALL}
            label={t.result.forecastHeading}
            text={interpretationToHtml(parsed.forecast)}
          />
        ) : null}
      </>
    );
  }

  const cards = toTarotCardItems(reading.cards);
  return (
    <>
      <ResultBlock
        icon={CRYSTAL_BALL}
        label={t.result.interpretation}
        text={interpretationToHtml(reading.interpretation)}
      />

      {cards.length > 0 ? (
        <CardList cards={cards} heading={t.result.cardsHeading} headingIcon={SPARKLES} />
      ) : null}
    </>
  );
}

function ResultBody({ reading, showPageTitle = true }: { reading: ReadingDetail; showPageTitle?: boolean }) {
  const navigate = useNavigate();

  return (
    <>
      <header className={styles.titleBlock}>
        {showPageTitle ? <h1 className={styles.title}>{t.result.title}</h1> : null}
        <p className={styles.date}>{formatSpreadCompletedRu(reading.createdAt)}</p>
      </header>

      {reading.userQuestion ? (
        <ResultBlock
          icon={String.fromCodePoint(0x2753)}
          label={t.result.yourQuestion}
          text={escapeHtml(reading.userQuestion)}
        />
      ) : null}

      <InterpretationBlocks reading={reading} />

      <RatingRow readingId={reading.id} initialRating={reading.rating ?? null} />
      <GoldButton className={styles.cta} onClick={() => navigate('/spread')}>
        {t.nav.newSpread}
      </GoldButton>
    </>
  );
}

export function ResultPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [params] = useSearchParams();
  const readingId = parseReadingId(params.get('id'));
  const { data, isLoading, isError } = useReading(readingId);
  const navigate = useNavigate();

  let mainBlocks: ReactElement;
  if (!readingId) {
    mainBlocks = (
      <div className={styles.titleBlock}>
        <h1 className={styles.title}>{t.result.notSelectedTitle}</h1>
        <p className={styles.date}>{t.result.notSelectedSub}</p>
        <GoldButton className={styles.cta} onClick={() => navigate('/spread')}>
          {t.nav.newSpread}
        </GoldButton>
      </div>
    );
  } else if (isLoading) {
    mainBlocks = (
      <div className={styles.titleBlock}>
        <h1 className={styles.title}>{t.common.loading}</h1>
      </div>
    );
  } else if (isError || !data) {
    mainBlocks = (
      <div className={styles.titleBlock}>
        <h1 className={styles.title}>{t.result.loadFailed}</h1>
        <GoldButton className={styles.cta} onClick={() => navigate('/')}>
          {t.result.toHome}
        </GoldButton>
      </div>
    );
  } else {
    mainBlocks = <ResultBody reading={data} showPageTitle={isDesktop} />;
  }

  const mobileHero = (
    <header className={styles.mobileHero}>
      <BackButton onClick={() => navigate('/spread')}>
        <>
          <span aria-hidden>←</span> {t.result.backToSpreads}
        </>
      </BackButton>
      <h1 className={styles.mobileTitle}>{t.nav.result}</h1>
    </header>
  );

  return (
    <div className={[anim.fadeIn, !isDesktop ? styles.mobilePage : ''].filter(Boolean).join(' ')}>
      {isDesktop ? (
        <div className={styles.desktopGrid}>
          <div className={styles.mainCol}>
            <section className={styles.body}>{mainBlocks}</section>
          </div>
          <DesktopResultAside />
        </div>
      ) : (
        <>
          {mobileHero}
          <div className={styles.mobileCol}>
            <section className={styles.body}>{mainBlocks}</section>
            <div className={styles.mobileAside}>
              <DesktopResultAside />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
